
import type React from "react"

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { format } from "date-fns"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useTrainer } from "@/contexts/trainer-context"
import { mockUsers } from "@/lib/mock-data"

interface AddAppointmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedDate: Date
  trainerId: string
}

export function AddAppointmentDialog({ open, onOpenChange, selectedDate, trainerId }: AddAppointmentDialogProps) {
  const { t } = useTranslation()
  const { addAppointment } = useTrainer()
  const [userId, setUserId] = useState("")
  const [startTime, setStartTime] = useState("09:00")
  const [endTime, setEndTime] = useState("10:00")
  const [type, setType] = useState("Strength Training")
  const [notes, setNotes] = useState("")

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    addAppointment({
      trainerId,
      userId,
      date: format(selectedDate, "yyyy-MM-dd"),
      startTime,
      endTime,
      status: "scheduled",
      notes,
      type,
    })

    // Reset form and close dialog
    setUserId("")
    setStartTime("09:00")
    setEndTime("10:00")
    setType("Strength Training")
    setNotes("")
    onOpenChange(false)
  }

  const appointmentTypes = [
    "Strength Training",
    "Cardio",
    "Flexibility",
    "HIIT",
    "Yoga",
    "Nutrition Consultation",
    "Assessment",
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("trainerView.addAppointment.title")}</DialogTitle>
            <DialogDescription>
              {t("trainerView.addAppointment.description", { date: format(selectedDate, "EEEE, MMMM d, yyyy") })}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="client" className="text-right">
                {t("trainerView.addAppointment.client")}
              </Label>
              <Select value={userId} onValueChange={setUserId} required>
                <SelectTrigger id="client" className="col-span-3">
                  <SelectValue placeholder={t("trainerView.addAppointment.selectClient")} />
                </SelectTrigger>
                <SelectContent>
                  {mockUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="start-time" className="text-right">
                {t("trainerView.addAppointment.startTime")}
              </Label>
              <Input
                id="start-time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="end-time" className="text-right">
                {t("trainerView.addAppointment.endTime")}
              </Label>
              <Input
                id="end-time"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="type" className="text-right">
                {t("trainerView.addAppointment.type")}
              </Label>
              <Select value={type} onValueChange={setType} required>
                <SelectTrigger id="type" className="col-span-3">
                  <SelectValue placeholder={t("trainerView.addAppointment.selectType")} />
                </SelectTrigger>
                <SelectContent>
                  {appointmentTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="notes" className="text-right">
                {t("trainerView.addAppointment.notes")}
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="col-span-3"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit">{t("trainerView.addAppointment.save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
